// Shared patient/Rx search used by the app bar search field and by any other
// place that lets the user pick a patient (e.g. the ticket linkage editor).
//
// The whole point of this module is that every patient picker in the app
// behaves identically: results of the location the user currently operates in
// come first and are grouped separately from the rest.

import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { BaseResultDtoKindEnum, SearchResponseDto } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { useLocationId } from "../User/User.hooks";
import { useDebouncedValue } from "../../utils/useDebounce";

export type PatientSearchGroup = "Here" | "Other locations";

export const PATIENT_SEARCH_GROUP_LABELS: Record<PatientSearchGroup, string> = {
  Here: "Aktueller Standort",
  "Other locations": "Andere Standorte",
};

export const EMPTY_LOCAL_RESULTS_ID = "empty-placeholder";

export interface PatientSearchOption {
  kind: BaseResultDtoKindEnum;
  id: number | string; // patientId or prescriptionId
  label: string; // composed display label
  firstName?: string;
  lastName?: string;
  dob?: string; // ISO date
  patientId?: number; // always populated for kind==="patient"; for prescription, the owner patient id
  prescriptionId?: string; // populated for kind==="prescription"
  locationId?: number; // location ID as number
  locationName?: string;
  locationMatch: boolean; // true if equals the current locationId
  group: PatientSearchGroup; // derived for UI grouping
  isEmptyPlaceholder?: boolean; // flag for empty group placeholder
}

interface UsePatientSearchOptions {
  limit?: number;
  /** Drop prescription hits — pickers that can only link a patient want this. */
  patientsOnly?: boolean;
  enabled?: boolean;
}

const DEBOUNCE_MS = 200;
const DEFAULT_LIMIT = 100;

export const formatSearchDob = (dob?: string) =>
  dob ? moment(dob).format("DD.MM.YYYY") : "—";

/**
 * Label of a patient hit. Exported so a pre-selected patient can be rendered
 * exactly like the same patient coming back from the search.
 */
export const buildPatientSearchLabel = (p: {
  firstName?: string;
  lastName?: string;
  dob?: string;
  patientId?: number | string;
}) => `${p.lastName ?? ""}, ${p.firstName ?? ""} • ${formatSearchDob(p.dob)} (${p.patientId ?? ""})`;

export const patientSearchKeys = {
  scope: "patientSearch",
  all: () => ["patientSearch"] as const,
  query: (q: string, locationId?: string, limit?: number) =>
    ["patientSearch", { q, locationId, limit }] as const,
};

const toOptions = (
  response: SearchResponseDto,
  patientsOnly: boolean,
): PatientSearchOption[] => {
  const hits = patientsOnly
    ? response.results.filter((r) => r.kind === BaseResultDtoKindEnum.Patient)
    : response.results;

  const options: PatientSearchOption[] = hits.map((r) => {
    const label =
      r.kind === BaseResultDtoKindEnum.Patient
        ? buildPatientSearchLabel({
            firstName: r.firstName,
            lastName: r.lastName,
            dob: r.dob,
            patientId: r.patientId ?? (typeof r.id === "object" ? String(r.id) : r.id),
          })
        : `Rx ${r.prescriptionId ?? r.id} • ${r.lastName ?? ""}, ${r.firstName ?? ""}`;

    const locationMatch = r.locationMatch ?? false;

    return {
      kind: r.kind,
      id: r.patientId ?? r.prescriptionId ?? (typeof r.id === "object" ? String(r.id) : r.id),
      label,
      firstName: r.firstName,
      lastName: r.lastName,
      dob: r.dob,
      patientId: r.patientId,
      prescriptionId: r.prescriptionId,
      locationId: typeof r.locationId === "object" ? undefined : r.locationId,
      locationName: r.locationName,
      locationMatch,
      group: locationMatch ? "Here" : "Other locations",
    };
  });

  // Sort: Here first, then Other; alphabetically within each group.
  options.sort((a, b) => {
    if (a.locationMatch !== b.locationMatch) return a.locationMatch ? -1 : 1;
    return a.label.localeCompare(b.label);
  });

  // If there are results from other locations but none from the current one,
  // keep the "Aktueller Standort" group visible with an explicit placeholder so
  // the user understands why the hits are all from elsewhere.
  const hasLocalResults = options.some((o) => o.locationMatch);
  const hasOtherResults = options.some((o) => !o.locationMatch);

  if (!hasLocalResults && hasOtherResults) {
    options.unshift({
      kind: BaseResultDtoKindEnum.Patient, // dummy value
      id: EMPTY_LOCAL_RESULTS_ID,
      label: "Keine Ergebnisse am aktuellen Standort",
      locationMatch: true,
      group: "Here",
      isEmptyPlaceholder: true,
    });
  }

  return options;
};

/**
 * Debounced patient/Rx search, prioritised by the location the user is
 * currently working in.
 */
export const usePatientLocationSearch = (
  input: string,
  { limit = DEFAULT_LIMIT, patientsOnly = false, enabled = true }: UsePatientSearchOptions = {},
) => {
  const locationId = useLocationId();
  const { patients } = useApi();
  const debouncedInput = useDebouncedValue(input, DEBOUNCE_MS);
  const query = debouncedInput.trim();

  const { data, isFetching } = useQuery({
    queryKey: patientSearchKeys.query(query, locationId, limit),
    queryFn: async () => await patients.search(query, locationId, undefined, undefined, limit),
    enabled: enabled && query.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    meta: { errorMessage: "Suche konnte nicht ausgeführt werden" },
  });

  const options = useMemo(
    () => (query.length > 0 && data ? toOptions(data, patientsOnly) : []),
    [data, query, patientsOnly],
  );

  return {
    options,
    loading: query.length > 0 && (isFetching || input.trim() !== query),
    locationId,
  };
};
